import { Component, Input, OnDestroy, ChangeDetectorRef, Output, EventEmitter, SimpleChanges, OnChanges, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BcRestApi } from 'common/api/rest-api';
import { ActionPermissionType } from 'common/common/constants/permission-type-enum';
import { UserPermissionService } from '../../services/user-permissions/user-permission.service';
import { ChatParticipant } from 'common/api/models/chat-participant.model';
import { take } from 'rxjs/operators';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { JoinChatEvents } from 'common/common/constants/analytics-enums';
import { Subscription } from 'rxjs';
import { ChatItem } from 'common/workitems/interfaces/chat-item.interface';
import { WorkItemChat } from 'common/workitems/models/work-item-chat';
import { ChatMessagesService } from 'common/chat-messages/chat-messages.service';
import { UserContextService } from 'common/user-context/user-context.service';
import { WorkItemObservableCacheService } from 'components/work-item-lane-list/work-item-lane/work-item-widget/shared/services/work-item-observable-cache.service';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { DynamicQueryDecoratorService } from 'common/dynamicquery/dynamic-query-decorator.service';
import { DynamicQueryAdapterService } from 'common/dynamicquery/dynamic-query-adapter.service';

@Component({
  selector: 'app-join-chat',
  templateUrl: './join-chat.component.html',
  styleUrls: ['./join-chat.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class JoinChatComponent implements OnDestroy, OnChanges, OnInit {
  private static readonly DomParser = new DOMParser();

  public editorInstance;
  public editorInitSetting;
  public isChatJoined: boolean = false;
  public joinInProgress: boolean = false;
  public leaveInProgress: boolean = false;
  public hasJoinPermission: boolean = false;
  public participantsLoaded: boolean = false;
  public chat: ChatItem;
  public editorContent = { text: '' };

  private _chatInsertObservable: Subscription;
  private _chatUpdateObservable: Subscription;
  private _chatParticipantsInsertedSubscription: Subscription;
  private _chatParticipantsDeletedSubscription: Subscription;
  private _chatStoreSubscription: Subscription;
  private _isTyping: boolean = false;
  private _typingTimeout;
  private _cannedMessageID: string;

  @Input()
  chatId: string;

  @Input()
  hasFullScreenRc: boolean = false;

  @Output()
  joinStateChanged: EventEmitter<boolean> = new EventEmitter();

  constructor(private _chatMessages: ChatMessagesService,
              private _userContextService: UserContextService,
              private _bcRestApi: BcRestApi,
              private _translateService: TranslateService,
              private _changeDetector: ChangeDetectorRef,
              private _userPermissionService: UserPermissionService,
              private DynamicQuery: DynamicQueryAdapterService,
              private _dynamicQueryDecorator: DynamicQueryDecoratorService,
              private _bcAnalytics: BcAnalytics,
              private _workItemObservableCacheService: WorkItemObservableCacheService) {
    this.hasJoinPermission = this._hasJoinActionPermission();
  }

  ngOnInit() {
    this.editorInitSetting = this.hasFullScreenRc ? { toolbar: 'bold italic underline insertImage removeformat' } : { toolbar: 'bold italic underline link insertImage removeformat' };
  }

  ngOnDestroy(): void {
    this._clearObservables();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.chatId && changes.chatId.previousValue !== changes.chatId.currentValue) {
      this._clearObservables();
      this._observeChat();
      this._observeParticipants();
    }
  }

  private _clearObservables() {
    if (this._chatInsertObservable) {
      this._chatInsertObservable.unsubscribe();
      this._chatInsertObservable = null;
    }
    if (this._chatUpdateObservable) {
      this._chatUpdateObservable.unsubscribe();
      this._chatUpdateObservable = null;
    }

    if (this._chatParticipantsInsertedSubscription) {
      this._chatParticipantsInsertedSubscription.unsubscribe();
      this._chatParticipantsInsertedSubscription = null;
    }
    if (this._chatParticipantsDeletedSubscription) {
      this._chatParticipantsDeletedSubscription.unsubscribe();
      this._chatParticipantsDeletedSubscription = null;
    }
    if (this._chatStoreSubscription) {
      this._chatStoreSubscription.unsubscribe();
      this._chatStoreSubscription = null;
    }
  }

  private _observeChat() {
    this._chatStoreSubscription = this._workItemObservableCacheService.getWorkItemObservable(this.chatId, WorkItemTypes.CHAT).subscribe(chat => {
      this.chat = new WorkItemChat(chat);
      this._changeDetector.detectChanges();
    });
  }

  private _observeParticipants() {
    this.participantsLoaded = false;
    const personsQuery = this.DynamicQuery.getQuery('getParticipantsForWorkItem', { WorkItemID: this.chatId });
    const chatParticipantsObservable = this._dynamicQueryDecorator.create(personsQuery, event => event && event.data);
    chatParticipantsObservable.done().pipe(take(1)).subscribe(() => {
      this.participantsLoaded = true;
      this._changeDetector.detectChanges();
    });
    this._chatParticipantsInsertedSubscription = chatParticipantsObservable.inserted().subscribe(
      insertedPerson => this._chatParticipantInserted(insertedPerson)
    );
    this._chatParticipantsDeletedSubscription = chatParticipantsObservable.updated().subscribe(
      deletedPerson => this._chatParticipantUpdated(deletedPerson)
    );
  }

  get canJoinChat(): boolean {
    return this.hasJoinPermission && this._isChatInProgress(this.chat) && this._isChatAnswered(this.chat);
  }

  private _isChatInProgress(chat: ChatItem): boolean {
    return chat && !chat.Ended && !chat.Closed;
  }

  private _isChatAnswered(chat: ChatItem): boolean {
    return !!(chat && chat.LastAssignedAnswered && chat.OperatorID);
  }

  get isChatOwn(): boolean {
    return this.chat && this._userContextService.userContext && this.chat.OperatorID === this._userContextService.userContext.id && !this.chat.Closed;
  }

  public isEditorVisible(): boolean {
    return (this.isChatJoined || this.isChatOwn) && this._isChatAnswered(this.chat);
  }

  public joinChat(): void {
    this.joinInProgress = true;
    this._bcRestApi.joinChat(this.chatId).then(() => {
      this.joinInProgress = false;
      this._changeDetector.detectChanges();
    }).catch(() => {
      this.joinInProgress = false;
      this._changeDetector.detectChanges();
    });
    this._bcAnalytics.eventTrack(JoinChatEvents.JOIN);
  }

  public leaveChat(): void {
    this.leaveInProgress = true;
    this._bcRestApi.leaveChat(this.chatId).then(() => {
      this.leaveInProgress = false;
      this._changeDetector.detectChanges();
    }).catch(() => {
      this.leaveInProgress = false;
      this._changeDetector.detectChanges();
    });
    this._bcAnalytics.eventTrack(JoinChatEvents.LEAVE);
  }

  public onEditorSetupDone(editor): void {
    this.editorInstance = editor;

    this.editorInstance.settings.placeholder = this._translateService.instant('joinChat.editorPlaceholder');

    if (this._isTyping) {
      this.sendTyping(false);
    }
    this.editorInstance.on('keyUp', this.handleTypingOnKeyUp.bind(this));
  }

  public onEditorSubmit(editorContent: string): void {
    if (this._isMessageNotEmpty(editorContent)) {
      this.sendTyping(false);
      this.cancelIsTypingTimeout();

      const messageObj = {
        ChatID: this.chatId,
        // TODO Operator name is buggy on the end point: it does not use this value, but it should
        Name: 'Operator',
        Text: editorContent,
        CannedMessageID: this._cannedMessageID
      };

      this._chatMessages.sendMessage(messageObj);
      this._clearEditor();
    }
  }

  addCannedMessage(cannedMessage) {
    if (this.editorInstance) {
      this.editorInstance.insertContent(cannedMessage.text);
      this._cannedMessageID = cannedMessage.id;
    }
  }

  get editorAutoSaveStorageKey() {
    return `join-chat-${this.chatId}`;
  }

  private _isMessageNotEmpty(message: string): boolean {
    const replacedMessage = message.replace(/<(?:.|\n)*?>/gm, '').trim();
    const doc = JoinChatComponent.DomParser.parseFromString(message, 'text/html');
    const index = doc.querySelector('img');
    return !!index || replacedMessage !== '';
  }

  private _clearEditor(): void {
    this.editorInstance.setContent('');
    this._cannedMessageID = undefined;
  }

  handleTypingOnKeyUp(): void {
    if (!this._isTyping && this.editorInstance.getContent() !== '') {
      this.sendTyping(true);
      this.startIsTypingTimeout();
    } else if (this._isTyping) {
      this.cancelIsTypingTimeout();
      this.startIsTypingTimeout();
    }
  }

  startIsTypingTimeout(): void {
    this._typingTimeout = setTimeout(() => {
      this.sendTyping(false);
    }, 1000, false);
  }

  cancelIsTypingTimeout(): void {
    if (this._typingTimeout) {
      clearTimeout(this._typingTimeout);
      this._typingTimeout = null;
    }
  }

  sendTyping(typing): void {
    if (this.chat) {
      this._bcRestApi.setTypingChat(this.chatId, typing);
      this._isTyping = typing;
    }
  }

  private _chatParticipantInserted(chatParticipant: ChatParticipant): void {
    if (chatParticipant && chatParticipant.OperatorID === this._userContextService.userContext.id && !chatParticipant.Closed && !this.isChatJoined) {
      this.isChatJoined = true;
      this._changeDetector.detectChanges();
      this.joinStateChanged.emit(true);
    }
  }

  private _chatParticipantUpdated(chatParticipant: ChatParticipant): void {
    if (chatParticipant && chatParticipant.OperatorID === this._userContextService.userContext.id && chatParticipant.Closed && this.isChatJoined) {
      this.isChatJoined = false;
      this._changeDetector.detectChanges();
      this.joinStateChanged.emit(false);
    }
  }

  private _hasJoinActionPermission(): boolean {
    return this._userPermissionService.hasPermissionFor(ActionPermissionType.CHAT_JOIN);
  }
}
